/**
 * Automatic tags (W5): brand, model, gender and category tags FlowStock adds to a product
 * without a merchant-written rule. The rules live in the database (`merchant_auto_tags`,
 * dictionary table `catalog_models`); this file only reads the store setting and the
 * dashboard preview (`preview_auto_tags`), which uses the same SQL as the sync.
 */

export type AutoTagPart = 'brand' | 'model' | 'gender' | 'category';

export interface AutoTagSettings {
  enabled: boolean;
  brand: boolean;
  model: boolean;
  gender: boolean;
  category: boolean;
}

export const AUTO_TAG_PARTS: Array<{ key: AutoTagPart; label: string; example: string }> = [
  { key: 'brand', label: 'Brand', example: 'adidas, UGG' },
  { key: 'model', label: 'Model', example: 'Samba, Tasman, Air Max 90' },
  { key: 'gender', label: 'Gender', example: 'Women, Kids (only when the supplier says so)' },
  { key: 'category', label: 'Category', example: 'Sneakers, Slippers, Boots' },
];

/** `config_object.auto_tags`. Absent means OFF; each part defaults to on once enabled. */
export function readAutoTagSettings(config: any): AutoTagSettings {
  const s = config?.auto_tags ?? {};
  return {
    enabled: s.enabled === true,
    brand: s.brand !== false,
    model: s.model !== false,
    gender: s.gender !== false,
    category: s.category !== false,
  };
}

export interface AutoTagPreview {
  enabled: boolean;
  /** product_descriptions.id -> tags */
  tags: Record<string, string[]>;
}

/** Shape-checks the `preview_auto_tags` result; anything unexpected reads as "no tags". */
export function parseAutoTagPreview(data: unknown): AutoTagPreview {
  const d = (data ?? {}) as any;
  const tags: Record<string, string[]> = {};
  if (d.tags && typeof d.tags === 'object' && !Array.isArray(d.tags)) {
    for (const [id, list] of Object.entries(d.tags)) {
      if (Array.isArray(list)) {
        const clean = list.map((t) => String(t).trim()).filter(Boolean);
        if (clean.length > 0) tags[id] = clean;
      }
    }
  }
  return { enabled: d.enabled === true, tags };
}
